/* eslint-disable react/jsx-props-no-spreading */

import { observer } from 'mobx-react-lite';
import type { ChangeEvent } from 'react';
import { Icon, SelectField, SelectFieldProps } from '../../../../../components';
import { showConfirmDialog } from '../../../../../components/modals';
import { useAccount } from '../../../../../store';
import { errorHandler } from '../../../../../utils/errors';

export default observer(function KeyLabelField({
  displayEmpty = true,
  emptyTitle = 'Not set',
  labelCreating = false,
  onChange,
  value,
  ...rest
}: LabelSelectProps) {
  const account = useAccount();
  const { labels } = account.configurations;

  function getOptions() {
    const options = labels.getLabelOptions(<i>{emptyTitle}</i>);

    if (labelCreating) {
      options.push({
        label: (
          <>
            <Icon icon="plus" />
            Add new label
          </>
        ),
        value: '__add_new_label__',
      });
    }

    return options;
  }

  async function createNewLabel(): Promise<string> {
    const newLabelName = await showConfirmDialog({
      title: 'New label',
      content: 'Enter name for a new label:',
      textField: {
        label: 'Label name',
      },
      confirmButton: {
        icon: 'plus',
        title: 'Create label',
      },
    });

    if (newLabelName !== 'cancel') {
      labels.setNewLabel(newLabelName);
      await labels.addNewLabel();
    }

    return newLabelName;
  }

  async function onLabelSelected(e: ChangeEvent<HTMLInputElement>) {
    const selectedLabel = e.target.value;

    if (selectedLabel === '__add_new_label__') {
      createNewLabel()
        .then((label) => label !== 'cancel' && onChange?.(label))
        .catch(errorHandler('Cannot create new label'));
    } else {
      onChange?.(selectedLabel);
    }
  }

  return (
    <SelectField
      defaultValue=""
      displayEmpty={displayEmpty}
      onChange={onLabelSelected}
      options={getOptions()}
      sx={{ mt: 0, width: 'auto' }}
      value={value}
      {...(rest as SelectFieldProps)}
    />
  );
});

interface LabelSelectProps extends Omit<SelectFieldProps, 'onChange'> {
  emptyTitle?: string;
  labelCreating?: boolean;
  onChange?: (selected: string) => void;
  value?: string;
}
